import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import { QRCodeCanvas } from 'qrcode.react';
import {
  ShieldCheck, CheckCircle, AlertTriangle, RefreshCw, Loader2, Award,
  GraduationCap, Calendar, Hash, TrendingUp, Zap, Target, XCircle
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const CATEGORY_LABELS = {
  technical_knowledge: 'Technical Knowledge', problem_solving: 'Problem Solving',
  coding_ability: 'Coding Ability', analytical_thinking: 'Analytical Thinking',
  communication: 'Communication', practical_application: 'Practical Application',
  behavioural_readiness: 'Behavioural Readiness', learning_agility: 'Learning Agility',
  resume_claim_validation: 'Resume Claim Validation', overall_employability: 'Overall Employability',
};

const DECISION = {
  verified: { label: 'Verified', cls: 'text-emerald-300 border-emerald-400/40 bg-emerald-500/10', Icon: CheckCircle },
  provisional: { label: 'Provisionally Verified', cls: 'text-amber-300 border-amber-400/40 bg-amber-500/10', Icon: ShieldCheck },
  reassessment: { label: 'Re-assessment Recommended', cls: 'text-indigo-300 border-indigo-400/40 bg-indigo-500/10', Icon: RefreshCw },
  not_verified: { label: 'Not Verified', cls: 'text-red-300 border-red-400/40 bg-red-500/10', Icon: AlertTriangle },
};

const LEVEL_TONE = {
  Strong: 'text-emerald-300 border-emerald-400/30 bg-emerald-500/10',
  Intermediate: 'text-sky-300 border-sky-400/30 bg-sky-500/10',
  Moderate: 'text-amber-300 border-amber-400/30 bg-amber-500/10',
  'Needs Improvement': 'text-red-300 border-red-400/30 bg-red-500/10',
};

const fmtDate = (d) => (d ? new Date(d).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' }) : '—');

const VerifiedReportPage = () => {
  const { vid } = useParams();
  const [loading, setLoading] = useState(true);
  const [report, setReport] = useState(null);
  const [error, setError] = useState('');

  useEffect(() => {
    (async () => {
      try {
        const res = await axios.get(`${API}/api/verified-talent/report/${vid}`);
        setReport(res.data);
      } catch (e) {
        setError(e.response?.status === 404 ? 'This verification ID was not found.' : 'Could not load this report.');
      } finally {
        setLoading(false);
      }
    })();
  }, [vid]);

  if (loading) {
    return <div className="min-h-screen bg-[#07070B] flex items-center justify-center text-zinc-400 gap-2"><Loader2 className="animate-spin" size={18} /> Loading verification report…</div>;
  }
  if (error || !report) {
    return (
      <div className="min-h-screen bg-[#07070B] flex flex-col items-center justify-center text-center px-6" data-testid="report-error">
        <XCircle size={40} className="text-red-400 mb-3" />
        <p className="text-zinc-300">{error || 'Report unavailable.'}</p>
        <Link to="/verified-talent" className="mt-4 text-sm text-purple-300 hover:text-purple-200">Learn about EvalentX Verified →</Link>
      </div>
    );
  }

  const decision = DECISION[report.decision] || (report.verified ? DECISION.verified : DECISION.not_verified);
  const scoreEntries = Object.entries(report.scores || {});
  const shareUrl = typeof window !== 'undefined' ? window.location.href : '';
  const initials = (report.candidate?.name || 'EX').split(' ').map((x) => x[0]).join('').slice(0, 2).toUpperCase();

  return (
    <div className="min-h-screen bg-[#07070B] text-white py-10 px-4" data-testid="verified-report-page">
      <div className="max-w-3xl mx-auto">
        {/* Header card */}
        <div className="rounded-3xl border border-white/10 bg-gradient-to-br from-[#0d0d16] to-[#0a0a12] p-7 md:p-9">
          <div className="flex items-center gap-2 text-[11px] uppercase tracking-[0.2em] text-purple-300 font-bold mb-6">
            <Award size={16} /> EvalentX Verified Employability Report
          </div>
          <div className="flex flex-col md:flex-row md:items-center gap-6">
            <div className="flex items-center gap-4 flex-1 min-w-0">
              {report.candidate?.avatar_url ? (
                <img src={report.candidate.avatar_url} alt="" className="w-16 h-16 rounded-2xl object-cover border border-white/10" />
              ) : (
                <div className="w-16 h-16 rounded-2xl bg-purple-500/20 border border-purple-400/30 flex items-center justify-center text-xl font-bold text-purple-200">{initials}</div>
              )}
              <div className="min-w-0">
                <h1 className="text-2xl font-bold truncate" style={{ fontFamily: "'Space Grotesk', sans-serif" }} data-testid="report-candidate-name">{report.candidate?.name || 'Candidate'}</h1>
                {report.candidate?.college && (
                  <p className="text-sm text-zinc-400 flex items-center gap-1.5 mt-0.5"><GraduationCap size={13} /> {report.candidate.college}</p>
                )}
              </div>
            </div>
            <div className="flex flex-col items-center gap-2">
              <div className="bg-white p-2 rounded-xl">
                <QRCodeCanvas value={shareUrl} size={92} level="M" />
              </div>
              <span className="text-[10px] text-zinc-500 uppercase tracking-widest">Scan to verify</span>
            </div>
          </div>

          <div className="mt-6 flex flex-wrap items-center gap-3">
            <div className={`inline-flex items-center gap-2 px-4 py-1.5 rounded-full border text-sm font-bold ${decision.cls}`} data-testid="report-decision">
              <decision.Icon size={15} /> {decision.label}
            </div>
            <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full border border-white/10 bg-white/[0.03] text-sm">
              <TrendingUp size={14} className="text-purple-300" /> Employability Score <strong className="text-white">{report.score}/100</strong>
            </div>
          </div>

          <div className="mt-5 grid grid-cols-2 md:grid-cols-3 gap-3 text-sm">
            <div className="rounded-xl border border-white/10 bg-white/[0.02] px-3 py-2.5">
              <p className="text-[10px] uppercase tracking-widest text-zinc-500 flex items-center gap-1"><Hash size={11} /> Verification ID</p>
              <p className="font-mono text-white text-xs mt-1" data-testid="report-verification-id">{report.verification_id}</p>
            </div>
            <div className="rounded-xl border border-white/10 bg-white/[0.02] px-3 py-2.5">
              <p className="text-[10px] uppercase tracking-widest text-zinc-500 flex items-center gap-1"><Calendar size={11} /> Verified on</p>
              <p className="text-white text-xs mt-1">{fmtDate(report.verified_on)}</p>
            </div>
            <div className="rounded-xl border border-white/10 bg-white/[0.02] px-3 py-2.5">
              <p className="text-[10px] uppercase tracking-widest text-zinc-500 flex items-center gap-1"><Calendar size={11} /> Valid until</p>
              <p className="text-white text-xs mt-1">{fmtDate(report.valid_until)}</p>
            </div>
          </div>
        </div>

        {/* Scorecard */}
        {scoreEntries.length > 0 && (
          <div className="mt-5 rounded-2xl border border-white/10 bg-white/[0.02] p-6" data-testid="report-scorecard">
            <div className="flex items-center gap-2 mb-4">
              <TrendingUp size={14} className="text-indigo-300" />
              <h3 className="text-[11px] font-bold tracking-[0.2em] uppercase text-zinc-300">Employability scorecard</h3>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-3">
              {scoreEntries.map(([k, v]) => {
                const sv = Math.max(0, Math.min(100, Number(v) || 0));
                return (
                  <div key={k}>
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-xs font-semibold text-zinc-200">{CATEGORY_LABELS[k] || k.replace(/_/g, ' ')}</span>
                      <span className="text-xs font-bold text-white">{Math.round(sv)}<span className="text-zinc-500">/100</span></span>
                    </div>
                    <div className="h-1.5 rounded-full bg-white/5 overflow-hidden">
                      <div className="h-full bg-gradient-to-r from-emerald-400 to-purple-400" style={{ width: `${sv}%` }} />
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Skills */}
        {(report.skill_competency || []).length > 0 && (
          <div className="mt-5 rounded-2xl border border-white/10 bg-white/[0.02] p-6" data-testid="report-skills">
            <div className="flex items-center gap-2 mb-3">
              <Zap size={14} className="text-amber-300" />
              <h3 className="text-[11px] font-bold tracking-[0.2em] uppercase text-zinc-300">Verified skills</h3>
            </div>
            <div className="flex flex-wrap gap-2">
              {report.skill_competency.map((s, i) => (
                <span key={i} className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border text-xs font-semibold ${LEVEL_TONE[s.level] || 'text-zinc-300 border-white/15 bg-white/5'}`}>
                  {s.skill}<span className="opacity-70">· {s.level}</span>
                </span>
              ))}
            </div>
          </div>
        )}

        {/* Recommended roles */}
        {(report.recommended_roles || []).length > 0 && (
          <div className="mt-5 rounded-2xl border border-white/10 bg-white/[0.02] p-6">
            <div className="flex items-center gap-2 mb-3">
              <Target size={14} className="text-emerald-300" />
              <h3 className="text-[11px] font-bold tracking-[0.2em] uppercase text-zinc-300">Recommended roles</h3>
            </div>
            <div className="flex flex-wrap gap-2">
              {report.recommended_roles.map((r, i) => (
                <span key={i} className="inline-flex items-center px-3 py-1.5 rounded-lg border border-white/15 bg-white/5 text-xs font-semibold text-zinc-200">{r}</span>
              ))}
            </div>
          </div>
        )}

        <p className="text-center text-[11px] text-zinc-600 mt-8">
          This report was generated by EvalentX's AI verification engine. Authenticity can be confirmed by scanning the QR code or visiting this URL.
        </p>
      </div>
    </div>
  );
};

export default VerifiedReportPage;
